/* eslint-disable class-methods-use-this,max-classes-per-file */

'use strict';

define('vb/private/events/eventBehavior',[
  'vb/private/constants',
  'vb/private/log',
  'vb/action/action',
  'vb/private/action/assignmentHelper',
  'vb/private/stateManagement/router',
  'vb/private/stateManagement/stateUtils',
  'vb/private/utils',
], (Constants, Log, Action, AssignmentHelper, Router, StateUtils, Utils) => {
  /**
   * @typedef {import('vb/private/stateManagement/container').default} Container
   * @typedef {import('vb/private/stateManagement/page').default} Page
   * @typedef {import('vb/private/events/eventRegistry').default} EventRegistry
   * @typedef {import('vb/private/events/eventModel').default} EventModel
   */

  const logger = Log.getLogger('/vb/private/events/eventBehavior');

  /**
   * EventBehavior
   *
   * Handles event propagation and custom behaviors for event listeners.
   *
   * Two main functionalities:
   *  A) `"behavior"` attribute - how the listeners are invoked, and results are returned.
   *  B) Propagation - order of containers and extensions.
   *
   * A) Behaviors:
   * implements the "behavior" of declared ('custom') events, via subclasses.
   *
   * The "behavior" can be one of the following:
   * - `notify`: Standard behavior, all container's listener chains called immediately, no waiting.
   * - `notifyAndWait`: Each container's listener chains are called serially.
   * - `checkForCancel`: Each container's listener chains are called serially; propagation stops when a chain returns
   *                     a "success" true with a payload of `{ stopPropagation: true }`.
   * - `transform`: The listener has an additional "$previous" variable which can be passed to the chain. The
   *                declaration has an optional additional "returnType". Each container's listener chains are called
   *                serially, and the result of each listener chain is the $previous of the next listener context.
   *
   * The container is responsible for finding the event in the registry, and calling EventBehavior.execute.
   *
   * Propagation:
   * Controls the order of container and extensions event processing. Propagation is 'lateral' within a container and
   * extensions, and 'vertical', up through container parents.
   *
   */
  class EventBehavior {
    /**
     * Creates an instance of EventBehavior.
     *
     * @param {String} name - The name of the event.
     * @param {EventModel} eventModel - The event model object that defines the event's properties.
     * @property {String} eventModel.behavior - The behavior type of the event (e.g., 'notify', 'notifyAndWait',
     * 'checkForCancel', 'transform' (deprecated), 'transformPayload', 'dynamicComponent').
     * @property {String} [eventModel.payloadType] - The expected type of the event payload.
     * @property {String} [eventModel.returnType] - The expected return type of the event.
     * @property {String} [eventModel.mode] - The mode of the event (e.g., 'listenable', 'triggerable').
     * @param {EventRegistry} registry - The event registry that manages all registered events.
     */
    constructor(name, eventModel, registry) {
      if (eventModel) {
        this.name = name;
        this.behavior = eventModel.behavior;
        this.payloadType = eventModel.payloadType;
        this.returnType = eventModel.returnType;
        this.mode = eventModel.mode;
        this.eventModel = eventModel;
      }

      this.registry = registry; // prevent circular requireJS dependency

      this.behavior = this.behavior || Constants.EventBehaviors.NOTIFY;
    }

    /**
     * Initiates event propagation from the specified firing container.
     *
     * This method determines the appropriate starting container for event propagation,
     * ensures the event is properly defined, and invokes the relevant event listener functions.
     *
     * @param {Container} firingContainer - The container that is initiating the event propagation.
     * @param {String} eventName - The name of the event to be propagated, with correct namespace.
     * @param {Object} eventPayload - The payload data to be passed to the event listeners.
     * @returns {Promise<undefined|Object>} - A promise resolving when the event propagation is complete.
     * The resolved value is either `undefined` or an object containing the result of the execution,
     * depending on the event behavior type. Rejects with an error if the event cannot be fired.
     */
    start(firingContainer, eventName, eventPayload) {
      let startingContainer;

      // if the current firing container is an extension
      // we need to check if the event is defined locally in the extension
      // that means it could either be defined in the firing container itself or
      // in its parent hierarchy
      // if that's the case, the startingContainer from where to start event
      // propagation should be the firingContainer
      if (firingContainer.isExtension()) {
        const eventModel = this.findEventDefinition(firingContainer, eventName, firingContainer);
        const eventDefinitionContainer = eventModel && eventModel.container;

        if (eventDefinitionContainer
            && (firingContainer === eventDefinitionContainer || eventDefinitionContainer.isExtension())) {
          startingContainer = firingContainer;
        }
      }

      if (!startingContainer) {
        // if there is no starting container, propagation should then start from
        // the firingContainer and next we will ensure event is defined before propagating
        // this covers very simple scenarios, like for example when event is both defined
        // and fired from Application or Flow as in this case there is no leaf container.
        startingContainer = this.startingContainer(firingContainer) || firingContainer;
      }

      // if the event is declared, or using a namespace, it is a 'new' style of event,
      // and always starts bubbling from the (lowest) current Page (or lowest valid container).
      if (startingContainer) {
        const eventModel = this.findEventDefinition(startingContainer, eventName, firingContainer);

        if (eventModel) {
          const coercedPayload = (eventModel && eventModel.payloadType)
            ? AssignmentHelper.coerceType(eventPayload, eventModel.payloadType)
            : eventPayload;

          // convenience object, for internal methods
          const eventWrapper = {
            callerName: eventName,
            name: this.eventModel.fullName,
            origin: firingContainer, // the one who fired
            payload: eventPayload,
            coercedPayload,
            eventModel,
          };

          // now get the listener functions
          const functionWrappers = this
            .recurseContainmentAndCreateFunctions(startingContainer, eventWrapper);

          return this.eventModel.eventBehavior.execute(functionWrappers);
        }

        return Promise.reject(new Error(`unable to fire event ${eventName}: no event model found `
          + `for ${startingContainer.className}, fired from ${firingContainer.className}.`));
      }
      return Promise.reject(new Error(`unable to fire event ${eventName}: no event source found, `
      + ` fired from ${firingContainer.className}.`));
    }

    /**
     * Determines the starting container for event propagation.
     *
     * This method returns the 'leaf' container from which event propagation should start.
     * It will typically be the most specific or deepest container in the hierarchy,
     * rather than the container that initially fired the event.
     *
     * If no container is provided, it falls back to the current page returned by the Router.
     *
     * @param {Container} container - The container from which to determine the starting point for event propagation.
     * @returns {Container|Page} - Returns the 'leaf' container to start from,
     * or the current page if no container is provided.
     */
    startingContainer(container) {
      if (container) {
        return container.getLeafContainer();
      }
      // fallback; shouldn't need this though
      return Router.getCurrentPage();
    }

    /**
     * Checks if the event can be listened to by the given container.
     *
     * This method determines whether the specified container is allowed to listen to the event.
     * It returns `true` if:
     * - The container is not an extension, or
     * - The event mode is 'triggerable', or
     * - The event mode is 'listenable'.
     *
     * @param {Container|Page} container - The container that is attempting to listen to the event.
     * @returns {boolean} - Returns `true` if the container is allowed to listen to the event, otherwise `false`.
     */
    listenableBy(container) {
      return (!container.isExtension()
        || this.mode === Constants.EventMode.TRIGGERABLE
        || this.mode === Constants.EventMode.LISTENABLE);
    }

    /**
     * Determines if the event can be triggered by the given container.
     *
     * This method checks whether the specified container is allowed to trigger the event. It returns `true` if:
     * - The container is not an extension container, or
     * - The container is the same as the container where the event is defined, or
     * - The event is declared as triggerable.
     *
     * @param {Container} container - The container that is attempting to trigger the event.
     * @returns {boolean} - Returns `true` if the event can be triggered by the container, otherwise `false`.
     */
    triggerableBy(container) {
      return !container.isExtension()
        || (this.eventModel && this.eventModel.container === container)
        || this.mode === Constants.EventMode.TRIGGERABLE;
    }

    /**
     * Searches for the event definition in the global registry by traversing the containment hierarchy.
     *
     * This method attempts to find the definition of the event specified by `eventName` starting from the given
     * container`. If the event definition is not found in the current container, it recursively looks in the parent
     * containers until the event definition is found or there are no more parent containers to check.
     *
     * @param {Container|Page} container - The container in which the search for the event definition begins.
     * @param {String} eventName - The name of the event to look for.
     * @param {Container} containerThatFired - The container that fired the event, used for scope/context lookup.
     * @returns {EventModel|undefined} - The event definition object if found, otherwise undefined.
     * @private
     */
    findEventDefinition(container, eventName, containerThatFired) {
      let eventDef = this.registry.get(container, containerThatFired, eventName);

      if (!eventDef) {
        const nextContainer = container.parent;
        if (nextContainer) {
          eventDef = this.findEventDefinition(nextContainer, eventName, containerThatFired);
        }
      }
      return eventDef;
    }

    /**
     * Calls all the chains defined for the event listener.
     * This method delegates to the internal `callChainFunctionsInternal` method to handle the execution and wraps the
     * results.
     *
     * @param {Container} container - The container in which the event listener chains are defined.
     * @param {Array<{chainId: String, fnc: function}>} chainFunctionWrappers - Array of objects where each object
     * contains a chain ID and a function representing the event listener chain.
     * @param {Object} expressionContexts - The contexts to be used while evaluating expressions within the event
     * listener chains.
     * @param {*} previousResult - The result from the previous chain, if any.
     * @returns {Promise<Array<{chainId: String, result: *}>>} - A Promise that resolves to an array of objects, where
     * each object contains a chain ID and its corresponding result.
     */
    // eslint-disable-next-line no-unused-vars
    callChainFunctions(container, chainFunctionWrappers, expressionContexts, previousResult) {
      return this.callChainFunctionsInternal(container, chainFunctionWrappers, expressionContexts, previousResult);
    }

    /**
     * Calls all the chains defined for the event listener. This method is invoked by `callChainFunctions`.
     * This is the 'default' behavior, and can be overridden by other behaviors.
     *
     * @param {Container} container - The container in which the event listener chains are defined.
     * @param {Array<{chainId: String, fnc: function}>} chainFunctionWrappers - Array of objects where each object
     * contains a chain ID and a function representing the event listener chain.
     * @param {Object} expressionContexts - The contexts to be used while evaluating expressions within the event
     * listener chains.
     * @param {*} previousResult - The result from the previous chain, if any.
     * @returns {Promise<Array<{chainId: String, result: *}>>} - A Promise that resolves to an array of objects,
     * where each object contains a chain ID and its corresponding result.
     * @protected
     */
    // eslint-disable-next-line no-unused-vars
    callChainFunctionsInternal(container, chainFunctionWrappers, expressionContexts, previousResult) {
      return container.callListenerChainFunctionsInParallel(chainFunctionWrappers, expressionContexts)
        // wrap each result with an object
        .then((results) => results && results
          .map((result, index) => ({ chainId: chainFunctionWrappers[index].chainId, result })));
    }

    /**
     * Calls the invokeEvent functions for a given container in the propagation 'chain'.
     * The functions are the set of invokeEvent() calls for the container tree,
     * created by Container.recurseContainmentAndCreateFunctions.
     *
     * The function signature should be: fnc(eventBehavior, previousValue).
     * Container curries the invokeEvent function (bind) to provide the name and payload.
     *
     * This delegates to executeInternal, but resolves with undefined.
     * Subclasses should only override if they need to return a result;
     * otherwise, they should override executeInternal.
     *
     * @param {Array<{ container: Container, fnc: function }>} functionWrappers - An array of objects where each object
     * contains a container and a function representing the event listener chain. The `fnc` function will be called
     * with the current instance of `EventBehavior`.
     * @returns {Promise<undefined>} - A promise that resolves when all functions in the `functionWrappers` have
     * completed executing. The resolved value is `undefined`.
     * @public
     */
    execute(functionWrappers) {
      logger.info('Triggering declared event, name:', this.name, 'behavior:',
        this.behavior, 'payloadType:', this.payloadType);
      return this.executeInternal(functionWrappers)
        .then(() => undefined);
    }

    /**
     * Called by `execute`, can be overridden by subclasses.
     *
     * This method handles the execution of the event listener functions defined in the provided `functionWrappers`.
     * By default, these functions are called in parallel.
     * Subclasses may override this method if different behavior is desired (e.g., serial execution,
     * conditional execution).
     *
     * This is invoked by the `execute` method.
     *
     * @param {Array<{ container: Container, fnc: function }>} functionWrappers - An array of objects where each object
     * contains a container and a function representing the event listener chain. The `fnc` function will be called
     * with the current instance of `EventBehavior`.
     * @returns {Promise<Array>} - A promise that resolves when all functions in the `functionWrappers` have completed
     * executing. The resolved value is an array of results returned by each function in the `functionWrappers`.
     * @protected
     */
    executeInternal(functionWrappers) {
      return Promise.all(functionWrappers.map((wrapper) => wrapper.fnc(this)));
    }

    /**
     * A utility method that conditionally coerces a given value to a specified type.
     * If the type is not specified, it returns the original value unchanged.
     *
     * @param {*} value - The value to be coerced to a specified type.
     * @param {String} [type] - The type to which the value should be coerced (e.g., 'string', 'boolean', 'number').
     * @returns {*} - The coerced value if the type is specified, otherwise the original value.
     */
    static shape(value, type) {
      return type ? AssignmentHelper.coerceType(value, type) : value;
    }

    /**
     * Creates an array of function wrappers that represent a curried version of container.invokeEvent.
     * The curried function represents: container.invokeEvent(name, coercedPayload).
     *
     * The caller (EventBehavior) provides the rest of the args (behavior, previous) at invocation.
     *
     * @param {Container|Page} container - The container in which the event listener is defined.
     * @param {EventModel} eventModel - The EventModel defining the event.
     * @param {Object} eventWrapper - An object containing details about the event.
     * @param {String} eventWrapper.name - The name of the event.
     * @param {Container} eventWrapper.origin - The container that fired the event.
     * @param {*} eventWrapper.payload - The payload of the event.
     * @param {*} eventWrapper.coercedPayload - The payload coerced to the defined payload type.
     * @param {EventModel} eventWrapper.eventModel - The EventModel defining the event.
     * @returns {Array<{ container: Container|Page, fnc: function }>} - An array of objects where each object contains a
     * container and a curried event invocation function.
     * @protected
     */
    curryListeners(container, eventModel, eventWrapper) {
      const functionWrappers = [];
      const fnc = container.invokeEvent.bind(container, eventWrapper.name, eventWrapper.coercedPayload);
      functionWrappers.push({
        container,
        fnc,
      });

      if (eventModel.isInterface) {
        const fncWrappers = this.recurseExtensionsAndCreateFunctions(container, eventWrapper);

        Array.prototype.push.apply(functionWrappers, fncWrappers);
      }

      return functionWrappers;
    }

    /**
     * Builds an array of asynchronous functions for event bubbling. Each function is a curried call to
     * container.invokeEvent, which returns a Promise. These functions are responsible for event propagation and can be
     * executed either in series or parallel.
     *
     * The wrapper for the event contains:
     *  - name: The event name, optionally qualified with container class prefix.
     *  - origin: The Container that is firing the event.
     *  - eventModel: The EventModel defining the event.
     *  - payload: Optional event-specific payload.
     *  - coercedPayload: The payload coerced to the defined payload type.
     *
     * @param {Container|Page} container - The current container initiating the event bubbling.
     * @param {Object} eventWrapper - An object containing details about the event.
     * @param {String} eventWrapper.name - The name of the event.
     * @param {Container} eventWrapper.origin - The container that fired the event.
     * @param {*} eventWrapper.payload - The payload of the event.
     * @param {*} eventWrapper.coercedPayload - The payload coerced to the defined payload type.
     * @param {EventModel} eventWrapper.eventModel - The EventModel defining the event.
     * @returns {Array<{ container: Container, fnc: function }>} - An array of objects where each object contains a
     * container and a curried event invocation function.
     */
    recurseContainmentAndCreateFunctions(container, eventWrapper) {
      const expressionContexts = container.getAvailableContexts().clone();
      expressionContexts[Constants.ContextName.EVENT] = eventWrapper.payload;

      const functionWrappers = [];
      let nextContainer;

      // if there is a definition anywhere in the chain,
      // we only invoke the event if we can see the event definition from the current container.
      const eventModel = this.registry.get(container, eventWrapper.origin, eventWrapper.name);

      if (eventModel) {
        // do not curry the invokeEvent if we are not allowed to listen to it
        if (this.listenableBy(container)) {
          // get the curried function for the event listeners for the (base) container, and siblings (extensions).
          // this may be overridden by the behavior to change the order, or to change if extensions are processed.
          const fncWrappers = this.curryListeners(container, eventModel, eventWrapper);
          Array.prototype.push.apply(functionWrappers, fncWrappers);
        }
      }

      // go to the next container...
      const terminalContainer = eventWrapper.eventModel.terminalContainer();
      // do not bubble to containers above the declaration of the event
      if (container !== terminalContainer) {
        nextContainer = this.getNextContainerForBubbling(
          container,
          eventWrapper.name,
          eventWrapper.eventModel,
          expressionContexts,
        );
      }

      if (nextContainer) {
        this.addFunctionWrappers(
          functionWrappers,
          this.recurseContainmentAndCreateFunctions(nextContainer, eventWrapper),
        );
      }

      // we are out of parents
      return functionWrappers;
    }

    /**
     * Ensures only one function wrapper per container is added.
     *
     * This method iterates through the source function wrappers and checks if their associated container already
     * exists in the target function wrappers. If the container does not already exist, it adds the function wrapper
     * to the target function wrappers list. This helps in avoiding duplication of function wrappers for the same
     * container when traversing through the extension hierarchy.
     *
     * @param {Array<{container: Container, fnc: function}>} targetFunctionWrappers - The array of function wrappers to
     * which new function wrappers will be added.
     * @param {Array<{container: Container, fnc: function}>} sourceFunctionWrappers - The array of function wrappers to
     * be checked and added to the target array if not already present.
     */
    addFunctionWrappers(targetFunctionWrappers, sourceFunctionWrappers) {
      const newFunctionWrappers = [];

      // need to ensure that the function wrappers for the same extension container are not added multiple times
      // this could happen because we traverse extension hierarchy of each base container and therefore
      // it's possible for different base containers to have the same parents in the extension hierarchy
      sourceFunctionWrappers.forEach((sourceFunctionWrapper) => {
        const foundContainer = targetFunctionWrappers
          .find((targetFunctionWrapper) => sourceFunctionWrapper.container === targetFunctionWrapper.container);

        if (!foundContainer) {
          newFunctionWrappers.push(sourceFunctionWrapper);
        }
      });

      Array.prototype.push.apply(targetFunctionWrappers, newFunctionWrappers);
    }

    /**
     * Iterates through any extensions and returns an array of curried invokeEvent functions.
     *
     * This method is similar to `Container.recurseContainmentAndCreateFunctions`, but specifically handles event
     * bubbling through container extensions.
     *
     * Each function returned is a curried version of `container.invokeEvent`, which includes the proper event name and
     * coerced payload. These functions ensure that events propagate correctly through extensions.
     *
     * @param {Container|Page} container - The container whose extensions will be checked for event listeners.
     * @param {Object} eventWrapper - An object containing details about the event.
     * @param {String} eventWrapper.name - The name of the event.
     * @param {Container} eventWrapper.origin - The container that fired the event.
     * @param {*} eventWrapper.payload - The payload of the event.
     * @param {*} eventWrapper.coercedPayload - The payload coerced to the defined payload type.
     * @param {EventModel} eventWrapper.eventModel - The EventModel defining the event.
     * @returns {Array<{ container: Container, fnc: function }>} - An array of objects where each object contains a
     * container and a curried event invocation function.
     */
    recurseExtensionsAndCreateFunctions(container, eventWrapper) {
      const functionWrappers = [];
      if (container.extensionsArray) {
        container.extensionsArray
          .forEach((extension) => {
            const fncWrappers = this.recurseContainmentAndCreateFunctions(extension, eventWrapper);
            Array.prototype.push.apply(functionWrappers, fncWrappers);
          });
      }

      return functionWrappers;
    }

    /**
     * Determines the next container in the sequence for event bubbling, ensuring that the event propagates
     * correctly through the containment hierarchy. Uses the `checkStopPropagationExpression` method to decide
     * whether to stop propagation based on certain conditions.
     *
     * @param {Container|Page} container - The current container where the event is being processed.
     * @param {String} eventName - The name of the event being processed.
     * @param {Object} eventModel - The event model object that defines the event's properties.
     * @param {Object} expressionContexts - The contexts to be used while evaluating expressions within the event
     * listener chains.
     * @returns {Container|null} - The next container in the chain for event bubbling,
     * or null if propagation should stop.
     * @private
     */
    getNextContainerForBubbling(container, eventName, eventModel, expressionContexts) {
      if (!this.checkStopPropagationExpression(container, eventName, eventModel, expressionContexts)) {
        return eventModel.autoWire
          ? this.getNextContainerForAutoWiredEventBubbling(container, eventModel)
          : container.parent;
      }
      return null;
    }

    /**
     * Gets the next container for bubbling the auto wired events.
     * Since the auto wired events cannot bubble up higher than its base parent container
     * and they (currently) always start from fragment, we will bubble up to the base parent
     * container of fragment. All extensions of the base container will be automatically
     * traversed when the event bubbles up to the base parent.
     *
     * @param {Container|Page} container - The current container where the event is being processed.
     * @param {Object} eventModel - The model object that defines the event's properties.
     * @returns {Container|null} - The next container to which the event should bubble,
     * or null if no further bubbling is required.
     * @private
     */
    getNextContainerForAutoWiredEventBubbling(container, eventModel) {
      if (eventModel.container === container && container.parent) {
        return container.parent.isExtension() ? container.parent.base : container.parent;
      }

      return null;
    }

    /**
     * Evaluates whether the 'stopPropagation' condition is true for a given event listener.
     *
     * This method checks the listener definition for a 'stopPropagation' property and evaluates its value.
     * If the property is set to true, event propagation will be stopped.
     * Note: Currently, this property is supported only on 'eventListeners' and not on 'events'.
     *
     * TODO: need to reconcile this with vbBeforeEnter, and result.cancelled: true
     *
     * @param {Container|Page} container - The container in which the event listener is defined.
     * @param {String} eventName - The name of the event.
     * @param {Object} eventModel - The model object that defines the event's properties.
     * @param {Object} expressionContexts - The contexts to be used while evaluating expressions within the event
     * listener chains.
     * @returns {boolean} - Returns true if the 'stopPropagation' condition evaluates to true, otherwise false.
     */
    checkStopPropagationExpression(container, eventName, eventModel, expressionContexts) {
      // support this property only on 'eventListeners', not 'events'
      const eventListener = container.findEventListener(eventName, eventModel);

      // evaluate listener stopPropagation values
      const stopped = (() => {
        if (eventListener && eventListener.stopPropagation) {
          const expr = StateUtils.getValueOrExpression(eventListener.stopPropagation, expressionContexts);
          return Utils.resolveIfObservable(expr);
        }
        return false;
      })();

      // log an 'info here
      if (stopped) {
        const msg = 'Event listener \'stopPropagation\' returned true';
        logger.info('Event bubbling stopped at container', container.name, 'for event', eventName, ':', msg);
      }
      return stopped;
    }
  }

  // used for constructing the results
  EventBehavior.ResultProperties = {
    RETURN: 'return',
  };

  return EventBehavior;
});

